import { useEffect, useRef, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/progress"
import { Select } from "@/components/ui/select"
import { getJSON } from "@/lib/http"
import { cn } from "@/lib/utils"
import {
  ADD_ON_TYPES,
  BID_LINE_TYPES,
  type CatalogItem,
  type CatalogSearchResponse,
  PRODUCT_TYPES,
} from "@/types/catalog"

// The debounced, abortable catalog search — ONE implementation shared by the
// product picker (flooring scope), the add-on dialog (which also reaches
// Misc), and the template-line picker (which also reaches Labor and Other
// Charge). Extracted in M8 so the search UIs cannot drift.
export function CatalogSearchList({
  active,
  scope = "flooring",
  picked,
  onPick,
}: {
  /** Search only while the host dialog is open. */
  active: boolean
  scope?: "flooring" | "add_ons" | "bid_line"
  picked: CatalogItem | null
  onPick: (item: CatalogItem) => void
}) {
  const [q, setQ] = useState("")
  const [type, setType] = useState("")
  const [includeInactive, setIncludeInactive] = useState(false)
  const [items, setItems] = useState<CatalogItem[]>([])
  const [available, setAvailable] = useState(true)
  const [loading, setLoading] = useState(false)
  const requestSeq = useRef(0)

  // Debounced, abortable search. The request counter guards against a slow
  // older response landing after a newer one.
  useEffect(() => {
    if (!active) return
    const seq = ++requestSeq.current
    const controller = new AbortController()
    setLoading(true)
    const timer = setTimeout(
      () => {
        const params = new URLSearchParams()
        if (q.trim()) params.set("q", q.trim())
        if (type) params.set("type", type)
        if (includeInactive) params.set("include_inactive", "1")
        if (scope !== "flooring") params.set("scope", scope)
        getJSON<CatalogSearchResponse>(`/catalog/items?${params}`, { signal: controller.signal })
          .then(({ data }) => {
            if (seq !== requestSeq.current) return
            setAvailable(data?.available ?? false)
            setItems(data?.available ? data.items : [])
            setLoading(false)
          })
          .catch(() => {
            if (seq !== requestSeq.current || controller.signal.aborted) return
            setAvailable(false)
            setItems([])
            setLoading(false)
          })
      },
      q.trim() ? 300 : 0
    )
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [active, q, type, includeInactive, scope])

  if (!available) {
    return (
      <div className="callout my-4">
        <p>
          The catalog isn&apos;t reachable right now. It may not be configured yet
          (the <code>fc_replica_database_url</code> credential) — rooms can still be
          traced and measured without products.
        </p>
      </div>
    )
  }

  const types =
    scope === "bid_line" ? BID_LINE_TYPES : scope === "add_ons" ? ADD_ON_TYPES : PRODUCT_TYPES

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search style, color, size — e.g. mohawk 12x24 gray"
          aria-label="Search the product catalog"
          className="flex-1"
        />
        <Select
          aria-label="Product type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="w-36"
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      </div>
      <label className="flex items-center gap-2 font-normal text-ink-body">
        <Checkbox
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Include inactive products
      </label>

      {/* FIXED height, not a max: the host dialogs are center-anchored, so a
          results box that grew and shrank with every keystroke (spinner →
          list → empty message) moved the whole dialog's edges each time.
          One constant viewport, scrolling inside; while a search is in
          flight the PREVIOUS results stay put, dimmed — the spinner only
          shows when there is nothing to keep on screen. */}
      <div
        className="h-64 overflow-y-auto rounded-md border border-hairline"
        data-testid="catalog-results"
      >
        {items.length === 0 ? (
          <div className="flex h-full items-center justify-center gap-2 p-6 text-center text-sm text-ink-muted">
            {loading ? (
              <>
                <Spinner /> Searching…
              </>
            ) : (
              "No products match — try fewer words or another type."
            )}
          </div>
        ) : (
          <ul
            className={cn(
              "divide-y divide-hairline transition-opacity",
              loading && "opacity-50"
            )}
          >
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onPick(item)}
                  className={cn(
                    "w-full px-3 py-2 text-left hover:bg-accent-faded",
                    picked?.id === item.id && "bg-accent-faded"
                  )}
                >
                  <span className="block truncate text-sm text-ink-display">
                    {[item.style, item.color].filter(Boolean).join(" · ") || item.id}
                    {!item.active && (
                      <Badge tone="muted" className="ml-2">
                        Inactive
                      </Badge>
                    )}
                  </span>
                  <span className="block truncate text-xs text-ink-muted">
                    {[
                      item.product_type,
                      item.format_label,
                      item.sqft_per_carton != null ? `${item.sqft_per_carton} sf/ctn` : null,
                      item.roll_width_ft != null ? `${item.roll_width_ft}' roll` : null,
                      item.vendor_name,
                      // Money pickers show the cost up front; the room picker
                      // stays quantity-only.
                      scope === "bid_line" && item.vendor_cost != null
                        ? `$${item.vendor_cost.toFixed(2)}/${item.unit ?? "unit"}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
